/**
 * React Query hooks for the notification system.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query'
import {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  deleteNotification,
} from './api'
import type { Notification, NotificationApiError } from './types'

export const notificationKeys = {
  all: ['notifications'] as const,
  list: () => [...notificationKeys.all, 'list'] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
}

/**
 * Fetch all notifications for the current user.
 */
export function useNotifications(
  options?: Omit<UseQueryOptions<Notification[], NotificationApiError>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: notificationKeys.list(),
    queryFn: getNotifications,
    staleTime: 30_000, // 30 seconds
    ...options,
  })
}

/**
 * Fetch the unread notification count.
 */
export function useUnreadCount(
  options?: Omit<UseQueryOptions<number, NotificationApiError>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: getUnreadCount,
    staleTime: 15_000, // 15 seconds
    refetchInterval: 60_000, // Poll every 60 seconds
    ...options,
  })
}

/**
 * Mark a single notification as read.
 */
export function useMarkAsRead(
  options?: UseMutationOptions<void, NotificationApiError, string>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
    ...options,
  })
}

/**
 * Mark all notifications as read.
 */
export function useMarkAllAsRead(
  options?: UseMutationOptions<void, NotificationApiError, void>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: markAllAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
    ...options,
  })
}

/**
 * Delete a notification.
 */
export function useDeleteNotification(
  options?: UseMutationOptions<void, NotificationApiError, string>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
    ...options,
  })
}
